/**
 * The rate-sheet price book, as a line-item editor consumes it.
 *
 * Feeds the Description picker on both ticket editors from
 * `GET /rate-sheets/items`, so a hand selects a priced service instead of
 * typing free text the price book does not contain and guessing the rate.
 *
 * WHAT THIS MODULE DOES NOT DO, AND WHY EACH ONE IS DELIBERATE
 * ------------------------------------------------------------
 * 1. It does not choose a rate sheet. The API returns every active sheet with
 *    `rateSheetId`/`rateSheetName` on every row precisely so the caller can
 *    show them all and let a person pick. `resolveRateSheet` on the API side
 *    refuses to choose between two company-wide books because a MODEL cannot be
 *    handed two mutually exclusive price lists — TNT's Casing Division book is
 *    turnkey and its Completions book is a la carte, and a blended draft that
 *    looks priced invoices a customer under a structure they never agreed to.
 *    A human reading a grouped list is not that situation: they are making
 *    exactly the choice the resolver says a person has to make.
 *
 *    `jobs.rate_sheet_id` therefore SORTS, and never filters — see
 *    `groupRateSheetItems`. It is NULL on every job in production (migration
 *    0332) and the job form's own copy tells operators to leave it that way, so
 *    a picker filtered on it would be empty on every ticket and would look
 *    exactly like a failed fetch.
 *
 * 2. It does not set the line item's Type. `rate_sheet_items.item_type` is a
 *    five-value vocabulary (service | equipment | labour | materials | other)
 *    and `ticket_line_items.line_item_type` is a nineteen-value Postgres enum.
 *    They are disjoint by design and nothing in either repo maps between them —
 *    the only thing that has ever bridged the two is Claude, inside
 *    `ticket-ai.service.ts`. Folding all five onto `other` would send every
 *    picked line down the AI-guess pay path, which is the defect
 *    `addLineItem`'s comment records as already fixed once. Type stays unset and
 *    the editors' existing "Type is required" validation makes the hand pick.
 *
 * 3. It DOES record which row the line came from — `rateSheetItemId`, since
 *    API 0406 (`ticket_line_items.rate_sheet_item_id`). That row is what picks
 *    the QuickBooks item on push: TNT's casing tong and integral tong are one
 *    `power_tongs` type and two QuickBooks items, and only the row tells them
 *    apart, so a power_tongs line with no row REFUSES to push.
 *
 *    EDITING KEEPS THE LINK. Changing the description or the rate afterwards
 *    does not touch `rateSheetItemId` — the QuickBooks item follows the service
 *    picked, not the wording. Only picking a row again replaces it. The id
 *    travels IN the line on every save, because every write door (and offline
 *    sync) replaces the line set: an id kept anywhere else is lost on the next
 *    save.
 */

import { normalizeUnit, unitLabel } from '../constants/units';
import { parseMoney } from './display-labels';

/**
 * One priced row, exactly as `GET /rate-sheets/items` projects it.
 *
 * Typed to the ACTUAL payload rather than to what a picker wishes it had — the
 * field-name-mismatch bug class in CLAUDE.md is three separate bugs of an
 * interface that declared a key the server never sent. Every name below is
 * copied from the `SELECT` in `listRateSheetItems`
 * (fieldiq-api/src/services/rate-sheet-resolution.service.ts).
 *
 * `serviceName` is the item's NAME and is NOT NULL. `description` is a nullable
 * secondary note holding detail — for TNT's first casing item the name is
 * `Surface Casing 7" – 9 5/8" (0–300 Meters)` and the description is
 * `Includes: 1 Operator, Power Unit, Tong, ...`. The description alone is not a
 * label and must never be used as one.
 *
 * `price` arrives as a string: node-pg hands back NUMERIC as text so a value
 * that does not fit a float is not silently rounded. Coerce with `parseMoney`,
 * never `parseFloat(x) || 0`.
 */
export interface RateSheetItem {
  id: string;
  serviceName: string;
  description: string | null;
  itemType: string | null;
  unit: string | null;
  customUnit: string | null;
  price: string | number | null;
  currency: string | null;
  revenueCode: string | null;
  displayOrder: number | null;
  rateSheetId: string;
  rateSheetName: string;
  rateSheetType: string | null;
  customerId: string | null;
}

/** The line-item fields a picked price book row is allowed to touch. */
export interface PricedLineItemShape {
  description: string;
  unit: string | null;
  rate: number | null;
  quantity: number | null;
  total: number | null;
  revenueCode: string | null;
  /** The rate-sheet row this line was priced from, or null — see note 3 above. */
  rateSheetItemId: string | null;
  /** 0504 off book; see lib/price-book-link.ts for why it may be undefined. */
  offBook?: boolean;
}

/** One rate sheet's rows, for a grouped menu. */
export interface RateSheetItemGroup {
  sheetId: string;
  sheetName: string;
  /** True when this is the sheet `jobs.rate_sheet_id` names. */
  preferred: boolean;
  items: RateSheetItem[];
}

/** The result of applying a picked row: the next line item, plus what could not be set. */
export interface AppliedRateSheetItem<T extends PricedLineItemShape> {
  next: T;
  /**
   * Non-null when the book's unit could not be resolved to one this app can
   * store. The caller MUST render it next to the row — see `resolvePickedUnit`.
   */
  unitNote: string | null;
}

/**
 * The book's unit, resolved to the canonical vocabulary — or refused, loudly.
 *
 * THIS IS THE PART THAT MOVES MONEY, so it refuses rather than guesses.
 * `normalizeUnit` carries an absolute digit guard: a unit carrying a quantity
 * (`5 day`, `per 12 hours`, `per 24 hour`) is a PERIOD rate and the canonical
 * vocabulary has no way to express a period. TNT's book holds 100 reachable
 * rows spelled that way, including 92 `5 day` handling-tool rentals whose
 * meaning — a five-day term versus a five-plus-day tier — is a fivefold billing
 * difference still open with the customer. Folding one onto `per_day` is a
 * multiplication error with nothing on screen to say it happened.
 *
 * So a refusal returns `''` with a note. `''` and not null: `''` is what the
 * write path already stores for "not set", it round-trips to the unit menu's
 * "Select..." state, and both insert sites pass `unit || null` into a NOT NULL
 * column — null there is a 23502 that loses the whole save.
 *
 * `custom` is routed through `customUnit`, because `custom` is a canonical value
 * on both sides and would otherwise resolve to itself while the actual unit text
 * ("10L Pail") was dropped in silence. Resolving the text instead either finds a
 * real unit or says it could not, which is the same contract as every other row.
 */
export function resolvePickedUnit(item: RateSheetItem): { unit: string; note: string | null } {
  const source = item.unit === 'custom' ? item.customUnit : item.unit;
  const raw = (source ?? '').toString().trim();
  if (!raw) return { unit: '', note: null };

  const resolved = normalizeUnit(raw);
  if (resolved) return { unit: resolved, note: null };

  return {
    unit: '',
    note: `The price book lists this as "${raw}", which is not a unit this ticket can store — pick one.`,
  };
}

/**
 * Apply a picked price book row to a line item.
 *
 * Sets Description, Rate and Revenue Code directly; Unit through the resolver
 * above; and recomputes Total from the new rate and the quantity already on the
 * row, matching what `updateLineItem` does when a rate is typed by hand.
 *
 * Quantity is untouched. It is the hand's measurement of the work, and the
 * price book has no opinion about it.
 *
 * An absent price becomes `null`, never `0`. A rate of zero that is really a
 * missing rate is indistinguishable from a real one on screen, so nobody ever
 * files a bug for it — the line just bills nothing.
 */
export function applyRateSheetItem<T extends PricedLineItemShape>(
  row: T,
  item: RateSheetItem
): AppliedRateSheetItem<T> {
  const { unit, note } = resolvePickedUnit(item);
  const rate = parseMoney(item.price);
  const quantity = row.quantity;

  return {
    next: {
      ...row,
      description: rateSheetItemLabel(item),
      unit,
      rate,
      revenueCode: item.revenueCode || null,
      // The one field the rest of the editor never writes: re-picking is the
      // only thing that changes it.
      rateSheetItemId: item.id,
      // A picked row takes the line back into the book. Left true, the save
      // would carry a link AND offBook, which every write door refuses (400).
      ...(row.offBook === true ? { offBook: false } : {}),
      total:
        rate != null && quantity != null
          ? Math.round(Number(rate) * Number(quantity) * 100) / 100
          : null,
    },
    unitNote: note,
  };
}

/**
 * What goes in the Description field.
 *
 * The NAME, not the note. `description` is only reached for when `serviceName`
 * is somehow empty — the column is NOT NULL, so that should never happen, but a
 * blank Description fails the editors' own save validation and a row that cannot
 * be saved is worse than one labelled from its detail line.
 */
export function rateSheetItemLabel(item: RateSheetItem): string {
  return (item.serviceName ?? '').trim() || (item.description ?? '').trim();
}

/**
 * The unit as the MENU shows it — the book's own wording, made readable.
 *
 * Canonical values get their proper label; anything else is rendered exactly as
 * stored. Never blanked and never rewritten: `rate_sheet_items.unit` is free
 * text with no CHECK constraint, TNT alone holds 24 spellings of about 12
 * concepts, and the menu's job is to show what the book says. The rewrite
 * happens once, on selection, where a refusal can be reported.
 */
export function rateSheetItemUnitText(item: RateSheetItem): string {
  const source = item.unit === 'custom' ? item.customUnit : item.unit;
  const raw = (source ?? '').toString().trim();
  if (!raw) return '';
  return unitLabel(raw) ?? raw;
}

/**
 * Rows matching every whitespace-separated term in `query`, in any field.
 *
 * Term-AND rather than one substring match: TNT's largest book is 172 rows and
 * a hand typing "tong casing" means both words, not the literal phrase — the
 * item is named `Power Tongs - Casing 2 3/8 - 9 5/8` and a phrase match finds
 * nothing. Matching is case-insensitive across the name, the detail note, the
 * sheet name and the revenue code, so a hand who knows the code can type it.
 *
 * An empty or whitespace-only query returns everything, unchanged and in the
 * order the API sent it.
 */
export function filterRateSheetItems(items: RateSheetItem[], query: string): RateSheetItem[] {
  const terms = (query ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return items;

  return items.filter((item) => {
    const haystack = [item.serviceName, item.description, item.rateSheetName, item.revenueCode]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}

/**
 * Rows grouped by the sheet they came from, the job's own sheet first.
 *
 * Order within and between groups is the API's — `rs.name`, then
 * `display_order NULLS LAST`, then `service_name` — and is not recomputed here.
 * Two answers to one ordering is the same class of problem as two answers to a
 * price.
 *
 * `preferredSheetId` is `jobs.rate_sheet_id`. It HOISTS that sheet's group and
 * marks it, so the picker can open it expanded; it never removes the others. A
 * job whose chosen sheet has since expired or been deactivated is not in this
 * list at all, and a filter would leave the hand staring at an empty menu with
 * no way to tell that from a broken request. Hoisting degrades to "no sheet is
 * first", which is the state every job is in today anyway.
 */
export function groupRateSheetItems(
  items: RateSheetItem[],
  preferredSheetId?: string | null
): RateSheetItemGroup[] {
  const groups: RateSheetItemGroup[] = [];
  const bySheet = new Map<string, RateSheetItemGroup>();

  for (const item of items) {
    let group = bySheet.get(item.rateSheetId);
    if (!group) {
      group = {
        sheetId: item.rateSheetId,
        sheetName: item.rateSheetName,
        preferred: !!preferredSheetId && item.rateSheetId === preferredSheetId,
        items: [],
      };
      bySheet.set(item.rateSheetId, group);
      groups.push(group);
    }
    group.items.push(item);
  }

  const preferred = groups.filter((g) => g.preferred);
  return preferred.length > 0 ? [...preferred, ...groups.filter((g) => !g.preferred)] : groups;
}

/** Total rows across a set of groups — for saying how many a cap left out. */
export function countGroupedItems(groups: RateSheetItemGroup[]): number {
  return groups.reduce((sum, group) => sum + group.items.length, 0);
}

/**
 * The first `limit` rows, kept in group order, plus how many were left out.
 *
 * TNT carries 311 reachable rows and a phone renders them badly. The cap is
 * REPORTED rather than silent: a truncated menu that says nothing reads as "the
 * price book only has this much", and a hand who cannot find their item stops
 * looking rather than typing one more word.
 */
export function capGroupedItems(
  groups: RateSheetItemGroup[],
  limit: number
): { groups: RateSheetItemGroup[]; hidden: number } {
  const total = countGroupedItems(groups);
  if (total <= limit) return { groups, hidden: 0 };

  // EVERY group gets a row before ANY group gets a second one.
  //
  // This used to fill group-by-group until the budget ran out, which was
  // harmless only for as long as `preferredSheetId` was NULL on every job. The
  // moment a job names a sheet, that sheet is hoisted to the front — and on
  // TNT the hoisted book can be the 172-item Completions one, which ate all 60
  // rows and left the other two books with ZERO. The menu then showed one book
  // and a "N more" count, which reads as "the others do not apply to this job":
  // exactly the narrowing `groupRateSheetItems` refuses to do, arrived at
  // through the cap instead.
  //
  // Order is untouched. The hoisted sheet is still first and still takes the
  // bulk of the budget; the others are merely no longer invisible.
  const take = groups.map(() => 0);
  let remaining = limit;

  for (let i = 0; i < groups.length && remaining > 0; i++) {
    if (groups[i].items.length > 0) {
      take[i] = 1;
      remaining -= 1;
    }
  }
  for (let i = 0; i < groups.length && remaining > 0; i++) {
    const extra = Math.min(groups[i].items.length - take[i], remaining);
    take[i] += extra;
    remaining -= extra;
  }

  const capped = groups
    .map((group, i) => ({ ...group, items: group.items.slice(0, take[i]) }))
    .filter((group) => group.items.length > 0);

  // Derived from what was actually kept, not from `limit` — the two diverge
  // when there are more groups than the budget can seat.
  return { groups: capped, hidden: total - countGroupedItems(capped) };
}
